import { Box, Editor, STROKE_SIZES, TLShape } from "tldraw";
import { convertTldrawIdToSimpleId } from "../../shared/format/convertTldrawShapeToBlurryShape";
import {
  CLUSTER_CUSHION_PX,
  InkBounds,
  clusterStrokes,
  unionBounds,
} from "./cluster";
import { changedInkClusters } from "./inkChanges";
import { labelsForInkClusters } from "./labels";
import { isStudentShape } from "../utils/studentShapeProtection";

/** Order-independent, so a restack does not read as a change. */
export function strokeKey(cluster: { strokeIds: string[] }): string {
  return [...cluster.strokeIds].sort().join(",");
}

/** One named unit of the student's handwriting, in PAGE space. */
export interface StudentInkCluster {
  label: string;
  bounds: { x: number; y: number; w: number; h: number };
  strokeIds: string[];
  /** Touched since the model last looked — one boolean for badge, crop, digest. */
  changed: boolean;
}

/**
 * The student's handwriting in `contextBounds`, grouped and named.
 *
 * SINGLE SOURCE for both the `inkClusters` prompt part (which tells the gateway
 * what to model and name) and the screenshot badges (which tell the agent which
 * name goes with which marks on the page). Those two must agree exactly — a
 * badge reading `ink_2` over writing the digest calls `ink_3` is worse than no
 * badge at all — so they call this, not their own copy of the rules.
 *
 * Safe to call more than once per turn: labels are inherited from the strokes
 * (see `labelForInkCluster`), so a second call over the same board is a no-op
 * that returns the same names.
 *
 * Clustering is PURELY SPATIAL, at `CLUSTER_CUSHION_PX`. On ruled
 * paper (RULE_SPACING 74, a line of writing ~45px tall) successive lines leave a
 * ~25-35px gap while the parts of one expression — numerator, bar, denominator —
 * sit within a few px of each other, so a 20px linking gap yields roughly one
 * cluster per line of writing. Edit time is deliberately not a factor: grouping
 * that shifted as timestamps aged would move labels between pieces of writing,
 * which is the one thing a stable name must never do.
 *
 * ORDER MATTERS between the two measurements below, and the mistake is silent.
 * Gaps are taken between the strokes' RAW geometry, and `inkedBounds` is applied
 * only to the cluster that comes out. Inflating first would shrink every
 * measured gap by two stroke widths, so the documented 20px vertical link would
 * really be `20 + 2w` — 27px at the default pen, 30px at the thick one, both
 * inside the 25-35px that separates two LINES of writing. The number would keep
 * its name and its comment while quietly chaining the lines it exists to keep
 * apart.
 */
export function computeStudentInkClusters(
  editor: Editor,
  owner: object,
  contextBounds: Box,
): StudentInkCluster[] {
  const strokes: {
    shapeId: string;
    bounds: InkBounds;
    lastEditedAt: number;
  }[] = [];
  // What each stroke INKS, kept beside the geometry rather than in place of it.
  // The two are used at different moments and must not be swapped: gaps are
  // measured between centrelines (below), extents between rendered pixels.
  const inked = new Map<string, InkBounds>();

  for (const shape of editor.getCurrentPageShapesSorted()) {
    if (!isStudentInk(shape)) continue;
    const geometry = editor.getShapeMaskedPageBounds(shape);
    if (!geometry) continue;
    if (!contextBounds.includes(geometry)) continue;
    const bounds = {
      x: geometry.x,
      y: geometry.y,
      w: geometry.w,
      h: geometry.h,
    };
    inked.set(
      convertTldrawIdToSimpleId(shape.id),
      inkedBounds(shape, geometry),
    );
    strokes.push({
      // SIMPLE id, the same one `blurryShapes` puts on the wire — the gateway
      // folds a cluster's members by looking each measured `shapeId` up in the
      // cluster's `strokeIds`, so a raw `shape:…` id here matches nothing: the
      // strokes stay unfolded as opaque rows and the cluster never gets its
      // measured bounds. Also the key the label registry inherits names by, so
      // both halves have to use one id vocabulary.
      shapeId: convertTldrawIdToSimpleId(shape.id),
      bounds,
      lastEditedAt: 0,
    });
  }
  if (strokes.length === 0) return [];

  const grouped = clusterStrokes(strokes, {
    gapPx: {
      x: CLUSTER_CUSHION_PX.x * 2,
      y: CLUSTER_CUSHION_PX.y * 2,
    },
    temporalMs: Number.POSITIVE_INFINITY,
  })
    .map((cluster) => ({
      strokeIds: cluster.map((s) => s.shapeId),
      // Inflated HERE, once per cluster, and never before the gap test above.
      // What the SCREENSHOT shows, not what the geometry says: everything
      // downstream — the bracket drawn around a cluster, the keep-out that
      // stops a label landing on it — is a claim about pixels, so it has to be
      // measured in pixels.
      bounds: unionBounds(cluster.map((s) => inked.get(s.shapeId) ?? s.bounds)),
    }))
    // Reading order, so a first-ever pass over a page numbers top-to-bottom.
    // Only cosmetic once labels exist — they are inherited, not re-derived.
    // Also the tie-break order when two clusters claim one inherited label.
    .sort((a, b) => a.bounds.y - b.bounds.y || a.bounds.x - b.bounds.x);

  // Labelled as a SET, not one at a time: a cluster that split needs both halves
  // in view to decide which keeps the old name, and a duplicate label would
  // collapse two pieces of writing into one element gateway-side.
  const labels = labelsForInkClusters(
    owner,
    grouped.map((cluster) => cluster.strokeIds),
  );

  const clusters = grouped.map(({ strokeIds, bounds }, index) => ({
    label: labels[index],
    bounds,
    strokeIds,
    changed: false, // set below; the diff needs every label settled first
  }));

  const changed = changedInkClusters(owner, clusters);
  for (const cluster of clusters) cluster.changed = changed.has(cluster.label);
  return clusters;
}

/**
 * The rectangle a stroke actually INKS, which is bigger than the one tldraw
 * reports.
 *
 * `getShapeMaskedPageBounds` measures a draw shape's geometry — the polyline
 * through the recorded points, i.e. the centre of the pen. What lands on the
 * screenshot is that path rendered with a stroke of `STROKE_SIZES[size]` and
 * fattened further by perfect-freehand's pressure taper, so the visible ink
 * overhangs the reported bounds on every side.
 *
 * Measured on a real board: brackets outset 2px from the geometry still had ink
 * pixels 1-2px OUTSIDE them, i.e. the dashed line was being drawn through the
 * outermost strokes of every digit — the exact corruption that turns a 3 into an
 * 8. One stroke width is the margin that clears it: half of it covers the stroke
 * itself and the rest absorbs the taper and the JPEG's antialiasing.
 *
 * Deliberately reads the shape's OWN size and scale rather than assuming the
 * defaults, so a student writing with the thick pen is measured with the thick
 * pen, and a shape scaled up is measured at the width it renders at.
 *
 * tldraw computes a related figure in `DrawShapeUtil.expandSelectionOutlinePx`
 * (`STROKE_SIZES[size] * 1.6 / 2 * scale`), and this deliberately does not call
 * it. It is excluded from the published release types, so it is not ours to
 * depend on; and it answers a different question — how far a SELECTION outline
 * stands off — returning `w/2 * scale` for a non-`draw` dash, half of what the
 * measurement above says the ink needs. The `scale` factor is the part worth
 * borrowing, and it is borrowed here.
 */
function inkedBounds(shape: TLShape, geometry: Box): InkBounds {
  const props = shape.props as
    | { size?: keyof typeof STROKE_SIZES; scale?: number }
    | undefined;
  const strokeWidth =
    (STROKE_SIZES[props?.size ?? "m"] ?? STROKE_SIZES.m) * (props?.scale ?? 1);
  return {
    x: geometry.x - strokeWidth,
    y: geometry.y - strokeWidth,
    w: geometry.w + 2 * strokeWidth,
    h: geometry.h + 2 * strokeWidth,
  };
}

/**
 * Freehand `draw` ink nobody but the student authored — the `meta.role` stamp
 * `blurryShapes` reads, so digest and screenshot cannot disagree. Teacher ink
 * (spaces only) is excluded: it is somebody else's handwriting.
 */
export function isStudentInk(shape: TLShape): boolean {
  if (shape.type !== "draw") return false;
  if (shape.meta?.hideFromAgent) return false;
  return isStudentShape(shape);
}
